import { useState } from "react";
import { cn } from "@/cn";
import { resolveCompanyLogoUrl } from "@/lib/company-logo-url";

const SIZE_CLASSES = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-lg",
} as const;

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export interface CompanyLogoProps {
  name: string;
  logoUrl?: string | null;
  domain?: string | null;
  size?: keyof typeof SIZE_CLASSES;
  className?: string;
}

export function CompanyLogo({
  name,
  logoUrl,
  domain,
  size = "md",
  className,
}: CompanyLogoProps) {
  const [failed, setFailed] = useState(false);
  const src = resolveCompanyLogoUrl({ logoUrl, domain });
  const sizeClass = SIZE_CLASSES[size];

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden
        onError={() => setFailed(true)}
        className={cn(
          "shrink-0 rounded-md border border-border bg-card object-contain p-0.5",
          sizeClass,
          className,
        )}
      />
    );
  }

  return (
    <div
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md border border-border bg-muted/30 font-semibold text-muted-foreground",
        sizeClass,
        className,
      )}
    >
      {initialsFromName(name)}
    </div>
  );
}
